import axios, { AxiosError } from "axios";
import { NextRouter } from "next/router";
import { sendUsingSocket } from "./webSocket";
import mixpanel from "./mixpanel";
import webengage from "@/webengage";
import { BootcampSlugData } from "@/types/bootcamp-slug";
import tracker from "@/utils/tracker";
import { RazorpayHandlerResponse } from "@/types/paymentTypes/razorpay";
import { UseGetAmountReturn } from "@/hooks/getAmount";

export const getBootcampBySlug = async (slug: string) => {
  try {
    const { data }: { data: BootcampSlugData } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}bootcamps/batch-details/${slug}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      }
    );
    return data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error fetching bootcamp by slug", error.response?.data);
    } else if (error instanceof Error) {
      console.error("Error fetching bootcamp by slug", error.message);
    } else {
      console.error("Error fetching bootcamp by slug", error);
    }
    return null;
  }
};
// PAYMENT TYPES FOR ORDER FLOW
export enum paymentType {
  PAID_MASTERCLASS = "paidMasterClass",
  PAID_BOOTCAMP = "paidBootcamp",
  VIP = "vip",
  TOKEN = "token",
  RECOVERY = "recovery",
  DISCOUNTED = "discounted",
  GOLD = "gold",
  GOLD_BOOTCAMP = "goldBootcamp",
  TOKEN_PLUS_GOLD = "token-plus-gold", // THIS CASE IS NOT VALID IN CART FLOW
}
//PAYMENT TYPES FOR CART FLOW
export enum PAYMENT_METHOD_TYPES {
  PAID_MASTERCLASS = "paidMasterClass",
  PAID_BOOTCAMP = "paidBootcamp",
  VIP = "vip",
  TOKEN = "token",
  RECOVERY = "recovery",
  DISCOUNTED = "discounted",
  GOLD = "gold",
  GOLD_BOOTCAMP = "goldBootcamp",
  TOKEN_RECOVERED = "token-recovered",
  GOLDPRODUCT = "goldProduct",
  PRODUCT = "product",
}

export enum PaymentTypeV2 {
  TOKEN = "TOKEN",
  RECOVERY = "RECOVERY",
  TOKEN_RECOVERED = "TOKEN_RECOVERED",
  DISCOUNTED = "DISCOUNTED",
  MANUAL = "MANUAL",
  FULL_PAYMENT = "FULL_PAYMENT",
}

export const purchaseBootcamp = async ({
  values,
  bootcamp,
  router,
  addPlan,
  paymentMethodType,
  userIp,
  finalCouponSelected,
  amount,
  selectedAddressId,
}: {
  values: {
    name: string;
    phone: string;
    email: string;
    address?: {
      country?: string;
      state?: string;
      pincode: string;
      addressLine1: string;
      city: string;
    };
  };
  bootcamp: any;
  router: NextRouter;
  addPlan: boolean;
  paymentMethodType?:
    | {
        newPaymentMethodType: PaymentTypeV2;
        oldPaymentMethodType: PAYMENT_METHOD_TYPES;
      }
    | undefined;
  userIp: string;
  finalCouponSelected: any;
  amount?: UseGetAmountReturn;
  selectedAddressId?: string | null;
}) => {
  const source = router.query.source ?? "website";
  const token = router.query.token ?? "";

  // @ts-ignore
  window.gtag("event", "payment_razorpay_init", {
    event_category: "payment",
    event_label: "payment_submit",
    value: "",
  });

  mixpanel.alias(values.phone);
  mixpanel.people.set({
    $name: values.name,
    $email: values.email,
    $phone: values.phone,
  });
  mixpanel.identify(values.phone);

  mixpanel.track("payment_initiate", {
    source: "Astrolearn",
    paymentStage: "razorpay_init",
    payment_type: paymentMethodType,
    bootcamp: bootcamp?.title,
  });

  const _webengage = webengage();
  if (_webengage) {
    _webengage.user.login(values.phone);
    _webengage.user.setAttribute("we_email", values.email);
    _webengage.user.setAttribute("we_phone", values.phone);
    _webengage.user.setAttribute("we_first_name", values.name);
    _webengage.track("Payment initiated", {
      title: bootcamp?.title,
      industry: "spirituality",
      fees:
        amount?.isToken || amount?.isDiscounted
          ? Number(amount?.mainButtonAmount)
          : Number(bootcamp?.discountedPrice),
      duration: bootcamp?.duration,
      instructor: bootcamp?.teacher.name,
      source,
      cohort: bootcamp?.startDateTime,
    });
  }

  if (values.phone && bootcamp?.id) {
    tracker(
      "trackSingleCustom",
      "InitiateCheckoutV2",
      undefined,
      `${values.phone}_${bootcamp.id}`
    );
  }

  const cartPayload = {
    bootcampProductIds: [bootcamp?.id],
    source: source,
    platform: "AstroLearn",
    paymentMethodType: paymentMethodType?.oldPaymentMethodType,
    ...(finalCouponSelected && { couponId: finalCouponSelected?.id }),
    token,
    user: {
      name: values.name,
      email: values.email,
      phone: values.phone,
    },
    ...(paymentMethodType?.oldPaymentMethodType ==
      PAYMENT_METHOD_TYPES.DISCOUNTED &&
      addPlan && { isDiscountedGold: true }),
    ...(selectedAddressId
      ? { addressId: selectedAddressId }
      : values.address && {
          address: {
            pincode: Number(values.address.pincode),
            addressLine1: values.address.addressLine1,
            city: values.address.city,
            state: values.address?.state,
            country: values.address?.country,
          },
        }),
  };
  try {
    const { data: orderData } = await axios.post(
      `${process.env.NEXT_PUBLIC_BASE_API}cart`,
      cartPayload
    );
    const { id, gatewayOrderId, totalPrice } = orderData.data;

    const isCartLevelCoupon =
      finalCouponSelected?.isGlobal ||
      finalCouponSelected?.isUserSpecificCoupon ||
      finalCouponSelected?.isCategoryCoupon;

    const createOrderPayload = {
      userPhone: values.phone,
      cartCouponCode: isCartLevelCoupon ? finalCouponSelected?.code : undefined,
      gatewayOrderId: gatewayOrderId,
      paymentMerchantId: process.env.NEXT_PUBLIC_PAYMENT_MERCHANT_ID!,
      cartItemCouponCodeMap: !isCartLevelCoupon
        ? {
            [bootcamp?.id]: finalCouponSelected?.code,
          }
        : undefined,
      platform: "ASTROLEARN",
      addressId: selectedAddressId ?? undefined,
      cartItems: [
        {
          entityId: bootcamp?.id,
          quantity: 1,
        },
      ],
      token: token,
      ...(paymentMethodType && {
        paymentType: paymentMethodType.newPaymentMethodType,
      }),
    };


    axios
      .post<{
        gatewayOrderId: string;
        paidPrice: number;
      }>(`${process.env.NEXT_PUBLIC_PAYMENTS_API}orders/`, createOrderPayload)
      .catch((err) => {
        console.log("Error in new payment architecture. Error creating order.");
        console.log(err);
      });

    sendUsingSocket({
      type: "payment",
      status: "pending",
    });

    const rzpOptions = {
      prefill: {
        name: values.name,
        email: values.email,
        contact: "+91" + String(values.phone),
      },
      key: process.env.NEXT_PUBLIC_RAZORPAY_KEY!,
      amount: Number(totalPrice),
      currency: "INR",
      name: `Bootcamp: ${bootcamp?.title}`,
      description: bootcamp?.title,
      image:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACkAAAAoCAYAAABjPNNTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAELElEQVRYhd2YP08cRxjGfzOz/9j1gmwMVJhEREGRXGAK6hSpaenS5yPQWIrc2UUkS3QuHajjwh8grpGQkUKBFJtghADnEHfc3p/ZmUlx3s1hJ+E4zlzsRxrtnXY189tn3nnn3RG8UxzHP2VZ9n2aphHgGJKstaJer+dJkvyitf6h3W7XAQjD8Md3YP+bJqV0cRz/DCCEECoMw9+azebXgBFCqI/lUi8SQiCEsMYYGUVRM8/zL6WU0tdaSwDP88QwAQGstRhjBIDv+wCxR1f85Xk+JLR/Vq1WQyllpRBi6O5dJDlsgF70SUB6g+pICMHt27cxxjA2NgZ0YkprjVJ/J4zx8XGazSbHx8cEQUC1Wv34kEIIpJSsrKzw4MED9vb2SJIEgEajgTGG7rBXStFqtbhz5w5LS0s8f/78wjGuPN3OOaSURbooUgjGGPI8x1p7ro2MjHBycoJSip2dnZ7GuLKTSim01tRqNQDq9Xp5L8syrLXnnDw5OWFhYYG1tTUODw+vB9Ja2+nI80roPM+ZmppiZmamvF9ISsnjx4+5f/9+T/E4EEjn3DmnTk9PWVxcZG1tjUePHhGG4bnnz87O2N7exlqL53k9bSADW92FgiAA4NWrV2xubp67V7yMc51NrgiF4v+1QbZaLQBu3br1AcD7MO+Hwr+pr9XdPb0FyOjoKACjo6M456hUKhc61Ksu7aTv+2itmZ+fp1qt4vs+WZaVuXFsbIw8z5menmZxcZGjoyOCIMA5x+vXr9FaXxpSeJ4XOee2jDFfAZYe3A2CgFqtVsYfQLVapdVqkWUZWmumpqZI0xRjTLnjTE5Ocnx83AuXAwTQVEp9c2knpZR4nsfW1hZKqXKF3rx5k3a7TRRFCCGoVCrs7u6itebevXusrq7y9u3byw4H9DHdzjlu3LhBEARMTExQq9XwfR8pJUEQlLuK1po0TZmensZay9OnT/sC7BuyW1JKwjAkSRLSNEVrTRiGZf5bX1/n4cOHvHz5kn5L1yunoEajwezsLMvLy7x48YJWq0WSJGUqqlarNJtN4MMXvDbIorB48+YNBwcHAFQqlQ+eC4KAdrvd1xhXroKKqsf3/f+czn4BYQCQxpirdnGhPonPh88XUkpJlmWcnp5y9+5dnj17xubmJnEcD5qvI8/zIqXUDp2tyNDDOU2SJC7LMmetdfv7+25ubm7QZ0H23bWhlPqirxQ0MjLC+vo6GxsbPHnyBGstURSV+XDg6sfJokVRVP72ff+jOXmlhdPtXD8lWK/6fFf3dauAHNrx8wVyzrmywJBA36XUINVdKUVRJPM8F16e580gCHaAWWOMuSzooD62uiToZBnPGLNnjPkDAN/354DfhRBOSnmpJoQYaAOK659xHH9bUEvAKqVSKeV3QoiYTp4axtw7KaUAjNb6V2PMASD/AtDPYOlV/rQjAAAAAElFTkSuQmCC",
      handler: async (response: RazorpayHandlerResponse) => {
        try {
          await axios.post(
            `${process.env.NEXT_PUBLIC_BASE_API}payments/authenticate/razorpay-payment`,
            {
              order_id: gatewayOrderId + "_0_bx",
              company: "ASTROJI",
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }
          );
          if (response.razorpay_order_id) {
            tracker(
              "trackSingleCustom",
              "PurchaseV2",
              undefined,
              response.razorpay_order_id
            );
          }
          router.push(`/payment/cart/${bootcamp?.slug}?intent-id=${id}`);
          mixpanel.track("bootcamp_purchased", {
            blog: bootcamp?.title,
            source: "Astrolearn",
            paymentMethodType,
          });
        } catch (error) {
          router.push(`/payment-not-verified`);
        }
      },
      order_id: gatewayOrderId,
      theme: {
        color: "#000",
      },
    };

    // @ts-ignore
    const rzp = new window.Razorpay(rzpOptions);
    rzp.open();
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Error in creating order", error.response?.data);
    } else if (error instanceof Error) {
      console.error("Error in creating order", error);
    } else {
      console.error("Error in creating order", error);
    }
  }
};
